import { apiFetch } from '../../../services/http';
import type {
  VideoProject,
  VideoScene,
  VideoReference,
  VideoVersion,
  FlowPackage
} from './videoStudioTypes';

function blobToDataUrl(blob: Blob | File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Falha ao ler arquivo de mídia'));
    reader.readAsDataURL(blob);
  });
}

export const videoStudioApi = {
  getVideoProject: (campaignId: string, creativeId: string, init?: RequestInit) =>
    apiFetch<{ ok: boolean; project: VideoProject }>(
      `/v1/crm/campaigns/${campaignId}/creatives/${creativeId}/video-projects`,
      init
    ),

  updateVideoProject: (campaignId: string, creativeId: string, data: Partial<VideoProject>, init?: RequestInit) =>
    apiFetch<{ ok: boolean; project: VideoProject }>(
      `/v1/crm/campaigns/${campaignId}/creatives/${creativeId}/video-projects`,
      {
        ...init,
        method: 'PUT',
        body: JSON.stringify(data),
      }
    ),

  updateScenes: (campaignId: string, creativeId: string, scenes: VideoScene[], init?: RequestInit) =>
    apiFetch<{ ok: boolean; scenes: VideoScene[] }>(
      `/v1/crm/campaigns/${campaignId}/creatives/${creativeId}/video-projects/scenes`,
      {
        ...init,
        method: 'PUT',
        body: JSON.stringify({ scenes }),
      }
    ),

  updateSceneStatus: (
    campaignId: string,
    creativeId: string,
    sceneOrder: number,
    status: string,
    extraPatch?: { video_url?: string | null; duration_seconds?: number },
    init?: RequestInit
  ) =>
    apiFetch<{ ok: boolean; scenes: VideoScene[] }>(
      `/v1/crm/campaigns/${campaignId}/creatives/${creativeId}/video-projects/scenes/${sceneOrder}/status`,
      {
        ...init,
        method: 'PATCH',
        body: JSON.stringify({ status, ...(extraPatch || {}) }),
      }
    ),

  narrateScene: (campaignId: string, creativeId: string, sceneOrder: number, dialogue?: string, init?: RequestInit) =>
    apiFetch<{ ok: boolean; narration_audio_url: string; scenes: VideoScene[]; message: string }>(
      `/v1/crm/campaigns/${campaignId}/creatives/${creativeId}/video-projects/scenes/${sceneOrder}/narrate`,
      {
        ...init,
        method: 'POST',
        body: JSON.stringify({ dialogue }),
      }
    ),

  updateReferences: (campaignId: string, creativeId: string, references: VideoReference[], init?: RequestInit) =>
    apiFetch<{ ok: boolean; references: VideoReference[] }>(
      `/v1/crm/campaigns/${campaignId}/creatives/${creativeId}/video-projects/references`,
      {
        ...init,
        method: 'PUT',
        body: JSON.stringify({ references }),
      }
    ),

  exportFlowPackage: (campaignId: string, creativeId: string, init?: RequestInit) =>
    apiFetch<{ ok: boolean; package: FlowPackage }>(
      `/v1/crm/campaigns/${campaignId}/creatives/${creativeId}/video-projects/export-package`,
      {
        ...init,
        method: 'POST',
      }
    ),

  importVideoVersion: (
    campaignId: string,
    creativeId: string,
    data: {
      video_url: string;
      thumbnail_url?: string | null;
      duration_seconds?: number | null;
      aspect_ratio?: string;
      review_notes?: string | null;
      is_selected?: boolean;
    },
    init?: RequestInit
  ) =>
    apiFetch<{ ok: boolean; version: VideoVersion; versions: VideoVersion[] }>(
      `/v1/crm/campaigns/${campaignId}/creatives/${creativeId}/video-projects/versions/import`,
      {
        ...init,
        method: 'POST',
        body: JSON.stringify(data),
      }
    ),

  selectVideoVersion: (campaignId: string, creativeId: string, versionId: string, init?: RequestInit) =>
    apiFetch<{ ok: boolean; selected_version: VideoVersion; versions: VideoVersion[] }>(
      `/v1/crm/campaigns/${campaignId}/creatives/${creativeId}/video-projects/versions/${versionId}/select`,
      {
        ...init,
        method: 'POST',
      }
    ),

  generateWithVeo: async (
    campaignId: string,
    creativeId: string,
    data?: {
      prompt?: string;
      duration_seconds?: number;
      api_key?: string;
      provider?: 'google' | 'replicate' | 'fal' | string;
      model_version?: 'veo-3.1' | 'veo-3.1-fast';
      resolution?: '720p' | '1080p' | '4k';
      scene_order?: number;
      dialogue?: string;
      scenes?: VideoScene[];
    },
    init?: RequestInit
  ): Promise<{
    ok: boolean;
    version?: VideoVersion;
    versions?: VideoVersion[];
    video_url?: string;
    error?: string;
    message?: string;
  }> => {
    try {
      return await apiFetch<{
        ok: boolean;
        version?: VideoVersion;
        versions?: VideoVersion[];
        video_url?: string;
        error?: string;
        message?: string;
      }>(
        `/v1/crm/campaigns/${campaignId}/creatives/${creativeId}/video-projects/generate-veo`,
        {
          ...init,
          method: 'POST',
          body: JSON.stringify(data || {}),
        }
      );
    } catch (err: any) {
      const msg = err.message || '';
      const cleanMsg = msg.replace(/^\d+:\s*/, '');
      const isSunset = cleanMsg.includes('descontinuou') || cleanMsg.includes('no longer available');
      return {
        ok: false,
        error: isSunset ? 'google_model_sunset' : 'veo_failed',
        message: cleanMsg || 'Falha ao renderizar com Google Veo',
      };
    }
  },

  uploadSceneVideo: async (
    campaignId: string,
    creativeId: string,
    sceneOrder: number,
    file: File | Blob,
    init?: RequestInit
  ): Promise<{
    ok: boolean;
    video_url?: string;
    asset_id?: string;
    scenes?: VideoScene[];
    project?: VideoProject;
    message?: string;
    error?: string;
  }> => {
    try {
      const dataUrl = await blobToDataUrl(file);
      return await apiFetch<{
        ok: boolean;
        video_url?: string;
        asset_id?: string;
        scenes?: VideoScene[];
        project?: VideoProject;
        message?: string;
        error?: string;
      }>(
        `/v1/crm/campaigns/${campaignId}/creatives/${creativeId}/video-projects/scenes/${sceneOrder}/upload`,
        {
          ...init,
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(init?.headers || {}),
          },
          body: JSON.stringify({ dataUrl }),
        }
      );
    } catch (err: any) {
      return {
        ok: false,
        error: 'upload_failed',
        message: err.message || 'Falha ao enviar arquivo de vídeo da cena',
      };
    }
  },

  stitchScenes: async (
    campaignId: string,
    creativeId: string,
    fileOrBlob: Blob | File,
    init?: RequestInit
  ): Promise<{
    ok: boolean;
    version?: VideoVersion;
    versions?: VideoVersion[];
    video_url?: string;
    project?: VideoProject;
    message?: string;
    error?: string;
  }> => {
    try {
      const dataUrl = await blobToDataUrl(fileOrBlob);
      return await apiFetch<{
        ok: boolean;
        version?: VideoVersion;
        versions?: VideoVersion[];
        video_url?: string;
        project?: VideoProject;
        message?: string;
        error?: string;
      }>(
        `/v1/crm/campaigns/${campaignId}/creatives/${creativeId}/video-projects/stitch`,
        {
          ...init,
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(init?.headers || {}),
          },
          body: JSON.stringify({ dataUrl }),
        }
      );
    } catch (err: any) {
      return {
        ok: false,
        error: 'stitch_failed',
        message: err.message || 'Falha ao juntar vídeos das cenas',
      };
    }
  },
};
